/**
 * The equipment register and planned maintenance. The register fills up as
 * IT works: a device is added the first time a ticket involves it, with as
 * little as a kind and a name, and details are completed later.
 */
import { z } from 'zod';
import { OFFICE_LOCATIONS } from './domain.ts';

export const DEVICE_KINDS = [
  'desktop',
  'laptop',
  'printer',
  'scanner',
  'multifunction',
  'photocopier',
  'router',
  'switch',
  'access_point',
  'modem',
  'firewall',
  'server',
  'ups',
  'projector',
  'phone',
  'other',
] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

export const DEVICE_KIND_LABELS: Record<DeviceKind, string> = {
  desktop: 'Desktop computer',
  laptop: 'Laptop',
  printer: 'Printer',
  scanner: 'Scanner',
  multifunction: 'Printer/scanner (multifunction)',
  photocopier: 'Photocopier',
  router: 'Router',
  switch: 'Network switch',
  access_point: 'Wi-Fi access point',
  modem: 'Modem / internet box',
  firewall: 'Firewall',
  server: 'Server / NAS',
  ups: 'UPS / power backup',
  projector: 'Projector / screen',
  phone: 'Phone / tablet',
  other: 'Other',
};

/** Kinds grouped for the pickers and filters. */
export const DEVICE_KIND_GROUPS: { label: string; kinds: DeviceKind[] }[] = [
  { label: 'Computers', kinds: ['desktop', 'laptop', 'server'] },
  { label: 'Printing', kinds: ['printer', 'scanner', 'multifunction', 'photocopier'] },
  { label: 'Network and internet', kinds: ['router', 'switch', 'access_point', 'modem', 'firewall'] },
  { label: 'Other', kinds: ['ups', 'projector', 'phone', 'other'] },
];

/** Devices that get an IP address field. */
export const NETWORK_KINDS: readonly DeviceKind[] = [
  'router',
  'switch',
  'access_point',
  'modem',
  'firewall',
  'server',
  'printer',
  'multifunction',
];

export const DEVICE_STATUSES = ['in_use', 'spare', 'in_repair', 'retired'] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];
export const DEVICE_STATUS_LABELS: Record<DeviceStatus, string> = {
  in_use: 'In use',
  spare: 'Spare',
  in_repair: 'Away for repair',
  retired: 'Retired',
};

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${max} characters or fewer.`)
    .nullish()
    .transform((v) => v || null);
const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-30.')
  .nullish()
  .or(z.literal('').transform(() => null))
  .transform((v) => v || null);

/** Only a kind and a name are needed; everything else can be filled in later. */
export const deviceSchema = z.object({
  kind: z.enum(DEVICE_KINDS, 'Choose what kind of device it is.'),
  name: z
    .string()
    .trim()
    .min(1, 'Give it a name people would recognise, e.g. “Finance HP printer”.')
    .max(100, 'Must be 100 characters or fewer.'),
  status: z.enum(DEVICE_STATUSES).default('in_use'),
  office: z
    .enum(OFFICE_LOCATIONS)
    .nullish()
    .transform((v) => v ?? null),
  /** Room or spot, e.g. "Accounts office, by the window". */
  location: optionalText(100),
  /** The person who uses it, for personal devices like laptops. */
  assignedToId: z
    .number()
    .int()
    .positive()
    .nullish()
    .transform((v) => v ?? null),
  make: optionalText(60),
  model: optionalText(80),
  serialNumber: optionalText(80),
  ipAddress: optionalText(60),
  purchasedOn: optionalDate,
  warrantyUntil: optionalDate,
  notes: optionalText(2000),
});
export type DeviceInput = z.infer<typeof deviceSchema>;

// ── Planned maintenance ─────────────────────────────────────────────

export const MAINTENANCE_UNITS = ['week', 'month'] as const;
export type MaintenanceUnit = (typeof MAINTENANCE_UNITS)[number];

/** How many days before the due date the reminder goes out. */
export const MAINTENANCE_REMIND_DAYS = 3;

export const maintenancePlanSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Say what needs doing, e.g. “Service the FTC printers”.')
    .max(120, 'Must be 120 characters or fewer.'),
  details: optionalText(2000),
  deviceId: z
    .number()
    .int()
    .positive()
    .nullish()
    .transform((v) => v ?? null),
  office: z
    .enum(OFFICE_LOCATIONS)
    .nullish()
    .transform((v) => v ?? null),
  every: z.number().int().min(1, 'At least 1.').max(52, 'At most 52.'),
  unit: z.enum(MAINTENANCE_UNITS),
  nextDue: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the next due date.'),
  /** Who does it, e.g. "Printer vendor". Can be left empty until someone is found. */
  responsible: optionalText(100),
  contactEmail: z
    .email('Please enter a valid email address.')
    .max(254)
    .nullish()
    .or(z.literal('').transform(() => null))
    .transform((v) => v || null),
  /** Email the reminders to the contact as well as IT. */
  emailContact: z.boolean().default(false),
  active: z.boolean().default(true),
});
export type MaintenancePlanInput = z.infer<typeof maintenancePlanSchema>;

export const maintenanceDoneSchema = z.object({
  doneOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the date it was done.'),
  doneBy: optionalText(100),
  notes: optionalText(2000),
});

/** Add weeks or months to a YYYY-MM-DD date. Months clamp to the month's last day (31 Jan + 1 month = 28/29 Feb). */
export function addInterval(date: string, every: number, unit: MaintenanceUnit): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  if (unit === 'week') {
    return new Date(Date.UTC(y, m - 1, d + 7 * every)).toISOString().slice(0, 10);
  }
  const target = new Date(Date.UTC(y, m - 1 + every, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

export const everyText = (every: number, unit: MaintenanceUnit) =>
  every === 1 ? `Every ${unit}` : `Every ${every} ${unit}s`;
